import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import { useAuthStore } from '../store/authStore';
import {
  Zap, TrendingUp, FileText, BarChart3, ArrowRight,
  CheckCircle2, ChevronDown, Star, Shield, Clock,
  Building2, Tractor, Truck, FlaskConical, Landmark,
  MapPin, Users, Calculator,
} from 'lucide-react';

// ─────────────────────────────────────────────
// DADOS
// ─────────────────────────────────────────────

const PROGRAMS = [
  { icon: Zap, name: 'BNDES Automático', desc: 'Máquinas, equipamentos e capital de giro associado' },
  { icon: Landmark, name: 'FINAME', desc: 'Financiamento exclusivo de máquinas e equipamentos' },
  { icon: FlaskConical, name: 'Finep & FAPs', desc: 'Inovação, P&D e subvenção econômica' },
  { icon: MapPin, name: 'Bancos Regionais', desc: 'BNB, BASA, Desenvolve SP, BDMG, BRDE' },
  { icon: Users, name: 'MEI & Autônomos', desc: 'Microcrédito e linhas para profissionais liberais' },
  { icon: Tractor, name: 'Crédito Rural', desc: 'PRONAF, PRONAMP e linhas do Plano Safra' },
  { icon: Truck, name: 'Renovação de Frota', desc: 'FINAME para veículos comerciais e agrícolas' },
  { icon: Building2, name: 'Fomento Estadual', desc: 'Programas específicos para cada estado' },
];

const FEATURES = [
  {
    icon: Zap,
    title: 'IA especializada',
    description: 'Respostas baseadas em 12 programas de financiamento reais, atualizadas com regras e taxas vigentes.',
  },
  {
    icon: Calculator,
    title: 'Simulador completo',
    description: 'Calcule parcelas, juros, capital de giro e payback em segundos. Exporte em PDF para levar ao banco.',
  },
  {
    icon: FileText,
    title: 'Checklist inteligente',
    description: '23 documentos organizados por prioridade. Saiba exatamente o que preparar antes de ir ao banco.',
  },
  {
    icon: BarChart3,
    title: 'Alertas e relatórios',
    description: 'Acompanhe vencimentos, renovações e receba alertas antes que sua máquina fique sem cobertura.',
  },
];

const TESTIMONIALS = [
  {
    name: 'Ricardo Almeida',
    role: 'Metalúrgica Almeida — SP',
    text: 'Em 20 minutos entendi toda a estrutura do BNDES Automático. Fui ao banco já sabendo o que pedir e aprovei R$ 280 mil em 30 dias.',
    stars: 5,
  },
  {
    name: 'Carla Mendonça',
    role: 'Clínica Odontológica — MG',
    text: 'Não sabia que existia FINAME para equipamentos odontológicos. O Soflexo me mostrou o caminho certo e ainda me ajudou a montar o dossiê.',
    stars: 5,
  },
  {
    name: 'João Ferreira',
    role: 'Produtor Rural — GO',
    text: 'Consegui entender as diferenças entre PRONAF e PRONAMP e qual era o mais vantajoso para minha situação. Economizei muito em juros.',
    stars: 5,
  },
];

const FAQS = [
  {
    q: 'O Soflexo consegue o financiamento para mim?',
    a: 'Não fazemos a operação — isso é feito no banco. O Soflexo te orienta para que você chegue ao banco preparado, sabendo qual linha pedir, quais documentos levar e o que negociar. Isso aumenta muito sua chance de aprovação.',
  },
  {
    q: 'Serve para qualquer tipo de empresa?',
    a: 'Sim. Cobrimos MEI, microempresas, pequenas, médias e grandes empresas, profissionais liberais e produtores rurais. A IA identifica seu perfil e indica os programas mais adequados para você.',
  },
  {
    q: 'As informações são confiáveis?',
    a: 'Nossa base de conhecimento é construída a partir das normas oficiais do BNDES, Finep, BNB, BASA e bancos de fomento estaduais. Sempre indicamos consultar o banco agente para confirmar condições atuais.',
  },
  {
    q: 'Posso cancelar quando quiser?',
    a: 'Sim, sem multa e sem burocracia. Você cancela pelo próprio painel a qualquer momento.',
  },
];

// ─────────────────────────────────────────────
// COMPONENTE
// ─────────────────────────────────────────────

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div style={{ fontFamily: "'Inter', 'Poppins', sans-serif", background: '#fff', color: '#0f172a' }}>

      {/* ── HEADER ── */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 50,
        background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(8px)',
        borderBottom: '1px solid #e2e8f0',
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: 'linear-gradient(135deg, #2563eb, #0891b2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Zap size={18} color="#fff" />
            </div>
            <span style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.5px' }}>Soflexo</span>
          </div>
          <nav style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {user ? (
              <button onClick={() => navigate('/dashboard')} style={btnPrimary}>Dashboard</button>
            ) : (
              <>
                <button onClick={() => navigate('/pricing')} style={btnGhost}>Planos</button>
                <button onClick={() => navigate('/login')} style={btnGhost}>Login</button>
                <button onClick={() => navigate('/register')} style={btnPrimary}>Começar grátis</button>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* ── HERO ── */}
      <section style={{ background: 'linear-gradient(160deg, #eff6ff 0%, #ecfeff 100%)', padding: '80px 24px 96px' }}>
        <div style={{ maxWidth: 800, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#dbeafe', borderRadius: 999, padding: '6px 16px', marginBottom: 24, fontSize: 13, fontWeight: 600, color: '#1d4ed8' }}>
            <Shield size={14} />
            12 programas de financiamento cobertos
          </div>
          <h1 style={{ fontSize: 'clamp(36px, 6vw, 64px)', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-1.5px', marginBottom: 24, color: '#0f172a' }}>
            Financiamento público para{' '}
            <span style={{ background: 'linear-gradient(90deg, #2563eb, #0891b2)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              qualquer empresa
            </span>
            , explicado de forma simples.
          </h1>
          <p style={{ fontSize: 18, color: '#475569', lineHeight: 1.7, marginBottom: 40, maxWidth: 600, margin: '0 auto 40px' }}>
            BNDES, FINAME, Finep, bancos regionais, crédito rural — o Soflexo orienta você a encontrar o programa certo, preparar a documentação e chegar ao banco pronto para aprovar.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/register')} style={{ ...btnPrimary, fontSize: 16, padding: '14px 32px', display: 'flex', alignItems: 'center', gap: 8 }}>
              Começar grátis <ArrowRight size={18} />
            </button>
            <button onClick={() => navigate('/pricing')} style={{ ...btnOutline, fontSize: 16, padding: '14px 32px' }}>
              Ver planos
            </button>
          </div>
          <p style={{ fontSize: 13, color: '#94a3b8', marginTop: 16 }}>
            Grátis para sempre • Sem cartão de crédito • 5 perguntas por dia no plano gratuito
          </p>
        </div>

        {/* Stats */}
        <div style={{ maxWidth: 700, margin: '64px auto 0', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24, textAlign: 'center' }}>
          {[
            { value: '12+', label: 'Programas cobertos' },
            { value: 'R$ 150M', label: 'Limite BNDES Automático' },
            { value: '23', label: 'Documentos no checklist' },
          ].map((s, i) => (
            <div key={i} style={{ background: '#fff', borderRadius: 16, padding: '20px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#2563eb', letterSpacing: '-1px' }}>{s.value}</div>
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── PROGRAMAS COBERTOS ── */}
      <section style={{ padding: '80px 24px', background: '#fff' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <p style={{ fontSize: 13, fontWeight: 600, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>Cobertura completa</p>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 800, letterSpacing: '-0.5px', marginBottom: 16 }}>
              Não só BNDES — todos os programas
            </h2>
            <p style={{ fontSize: 16, color: '#64748b', maxWidth: 560, margin: '0 auto' }}>
              A maioria dos consultores conhece 2 ou 3 linhas. O Soflexo cobre o ecossistema completo de crédito público brasileiro.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
            {PROGRAMS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={i} style={{ border: '1px solid #e2e8f0', borderRadius: 16, padding: '20px', display: 'flex', gap: 14, alignItems: 'flex-start', transition: 'border-color 0.2s', cursor: 'default' }}
                  onMouseEnter={e => (e.currentTarget.style.borderColor = '#93c5fd')}
                  onMouseLeave={e => (e.currentTarget.style.borderColor = '#e2e8f0')}
                >
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon size={20} color="#2563eb" />
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{p.name}</div>
                    <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>{p.desc}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section style={{ padding: '80px 24px', background: '#f8fafc' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <p style={{ fontSize: 13, fontWeight: 600, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>Funcionalidades</p>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 800, letterSpacing: '-0.5px' }}>
              Tudo que você precisa em um lugar
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 24 }}>
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} style={{ background: '#fff', borderRadius: 20, padding: '28px 24px', border: '1px solid #e2e8f0' }}>
                  <div style={{ width: 48, height: 48, borderRadius: 14, background: 'linear-gradient(135deg, #2563eb, #0891b2)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
                    <Icon size={22} color="#fff" />
                  </div>
                  <h3 style={{ fontWeight: 700, fontSize: 17, marginBottom: 10 }}>{f.title}</h3>
                  <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.6 }}>{f.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── POR QUE SOFLEXO ── */}
      <section style={{ padding: '80px 24px', background: '#fff' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 64, alignItems: 'center' }}>
          <div>
            <p style={{ fontSize: 13, fontWeight: 600, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>Por que Soflexo</p>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 36px)', fontWeight: 800, letterSpacing: '-0.5px', marginBottom: 32 }}>
              Consultores cobram R$ 3 mil a R$ 15 mil por projeto
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {[
                'Mais fácil que contratar um consultor',
                'Mais rápido que pesquisar no Google',
                'Mais barato que agências de fomento',
                'Disponível 24h, 7 dias por semana',
                'Personalizado para seu setor e estado',
                'Orientações baseadas em especialistas reais',
              ].map((b, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <CheckCircle2 size={20} color="#16a34a" style={{ flexShrink: 0 }} />
                  <span style={{ fontSize: 15, color: '#1e293b' }}>{b}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {[
              { label: 'Consultor tradicional', price: 'R$ 3.000–15.000', sub: 'por projeto', bad: true },
              { label: 'Soflexo Premium', price: 'R$ 99', sub: 'por mês', bad: false },
            ].map((c, i) => (
              <div key={i} style={{
                border: `2px solid ${c.bad ? '#e2e8f0' : '#2563eb'}`,
                borderRadius: 20, padding: '24px',
                background: c.bad ? '#fff' : '#eff6ff',
                position: 'relative',
              }}>
                {!c.bad && (
                  <div style={{ position: 'absolute', top: -12, right: 20, background: '#2563eb', color: '#fff', fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 999 }}>
                    Recomendado
                  </div>
                )}
                <div style={{ fontSize: 14, color: '#64748b', marginBottom: 8 }}>{c.label}</div>
                <div style={{ fontSize: 32, fontWeight: 800, color: c.bad ? '#94a3b8' : '#2563eb', letterSpacing: '-1px' }}>
                  {c.price}
                  <span style={{ fontSize: 15, fontWeight: 400, color: '#94a3b8' }}> {c.sub}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── DEPOIMENTOS ── */}
      <section style={{ padding: '80px 24px', background: '#f8fafc' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <p style={{ fontSize: 13, fontWeight: 600, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>Depoimentos</p>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 800, letterSpacing: '-0.5px' }}>
              Empresários que já aprovaram financiamento
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
            {TESTIMONIALS.map((t, i) => (
              <div key={i} style={{ background: '#fff', borderRadius: 20, padding: '28px 24px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', gap: 2, marginBottom: 16 }}>
                  {Array.from({ length: t.stars }).map((_, s) => (
                    <Star key={s} size={16} color="#f59e0b" fill="#f59e0b" />
                  ))}
                </div>
                <p style={{ fontSize: 15, color: '#1e293b', lineHeight: 1.7, marginBottom: 20 }}>"{t.text}"</p>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{t.name}</div>
                  <div style={{ fontSize: 13, color: '#64748b' }}>{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section style={{ padding: '80px 24px', background: '#fff' }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 800, letterSpacing: '-0.5px' }}>
              Perguntas frequentes
            </h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {FAQS.map((faq, i) => (
              <div key={i} style={{ border: '1px solid #e2e8f0', borderRadius: 16, overflow: 'hidden' }}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  style={{ width: '100%', padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                >
                  <span style={{ fontWeight: 600, fontSize: 15, color: '#0f172a' }}>{faq.q}</span>
                  <ChevronDown size={18} color="#64748b" style={{ transform: openFaq === i ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s', flexShrink: 0, marginLeft: 16 }} />
                </button>
                {openFaq === i && (
                  <div style={{ padding: '0 24px 20px', fontSize: 14, color: '#475569', lineHeight: 1.7 }}>
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA FINAL ── */}
      <section style={{ padding: '80px 24px', background: 'linear-gradient(135deg, #1e40af, #0e7490)' }}>
        <div style={{ maxWidth: 640, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 800, color: '#fff', letterSpacing: '-1px', marginBottom: 16 }}>
            Pronto para conseguir seu financiamento?
          </h2>
          <p style={{ fontSize: 17, color: '#bfdbfe', marginBottom: 40, lineHeight: 1.6 }}>
            Comece grátis agora e descubra em minutos qual programa é ideal para sua empresa.
          </p>
          <button onClick={() => navigate('/register')} style={{
            background: '#fff', color: '#1d4ed8', border: 'none', borderRadius: 12,
            padding: '16px 40px', fontSize: 16, fontWeight: 700, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 8,
          }}>
            Começar grátis <ArrowRight size={18} />
          </button>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 24, marginTop: 32, flexWrap: 'wrap' }}>
            {['Sem cartão de crédito', 'Cancele quando quiser', 'Suporte em português'].map((t, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#93c5fd' }}>
                <CheckCircle2 size={14} /> {t}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ background: '#0f172a', color: '#94a3b8', padding: '48px 24px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 32, marginBottom: 40 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <div style={{ width: 28, height: 28, borderRadius: 6, background: 'linear-gradient(135deg, #2563eb, #0891b2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Zap size={14} color="#fff" />
                </div>
                <span style={{ fontWeight: 700, color: '#fff', fontSize: 16 }}>Soflexo</span>
              </div>
              <p style={{ fontSize: 13, lineHeight: 1.6 }}>Inteligência financeira para empresários brasileiros.</p>
            </div>
            <div>
              <h4 style={{ color: '#fff', fontWeight: 600, fontSize: 14, marginBottom: 16 }}>Produto</h4>
              <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {['Simulador', 'Documentação', 'Planos'].map((l, i) => (
                  <li key={i}><a href="#" style={{ fontSize: 13, color: '#94a3b8', textDecoration: 'none' }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#fff')}
                    onMouseLeave={e => (e.currentTarget.style.color = '#94a3b8')}
                  >{l}</a></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 style={{ color: '#fff', fontWeight: 600, fontSize: 14, marginBottom: 16 }}>Empresa</h4>
              <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {['Sobre', 'Blog', 'Contato'].map((l, i) => (
                  <li key={i}><a href="#" style={{ fontSize: 13, color: '#94a3b8', textDecoration: 'none' }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#fff')}
                    onMouseLeave={e => (e.currentTarget.style.color = '#94a3b8')}
                  >{l}</a></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 style={{ color: '#fff', fontWeight: 600, fontSize: 14, marginBottom: 16 }}>Legal</h4>
              <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {['Privacidade', 'Termos'].map((l, i) => (
                  <li key={i}><a href="#" style={{ fontSize: 13, color: '#94a3b8', textDecoration: 'none' }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#fff')}
                    onMouseLeave={e => (e.currentTarget.style.color = '#94a3b8')}
                  >{l}</a></li>
                ))}
              </ul>
            </div>
          </div>
          <div style={{ borderTop: '1px solid #1e293b', paddingTop: 24, textAlign: 'center', fontSize: 13 }}>
            © 2025 Soflexo. Todos os direitos reservados.
          </div>
        </div>
      </footer>
    </div>
  );
}

// ─────────────────────────────────────────────
// ESTILOS INLINE REUTILIZÁVEIS
// ─────────────────────────────────────────────

const btnPrimary: React.CSSProperties = {
  background: 'linear-gradient(135deg, #2563eb, #0891b2)',
  color: '#fff',
  border: 'none',
  borderRadius: 10,
  padding: '10px 20px',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};

const btnGhost: React.CSSProperties = {
  background: 'none',
  color: '#475569',
  border: 'none',
  borderRadius: 10,
  padding: '10px 16px',
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
};

const btnOutline: React.CSSProperties = {
  background: '#fff',
  color: '#2563eb',
  border: '2px solid #2563eb',
  borderRadius: 10,
  padding: '10px 20px',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};